import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Link2, CheckCircle2, AlertTriangle, RefreshCw,
  ChevronRight, SlidersHorizontal, Search, HelpCircle, Plus,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import type { FortnoxStatus, HaileyStatus, TimeTrackerStatus, AzureCostStatus, CalendarificStatus, ClementineStatus } from '@/types/api'

// ── Catalogue metadata (static) ────────────────────────────────────────────

type CatalogueEntry = {
  id: string; name: string; monogram: string; gradient: string
  category: string; tags: string[]; description: string; href?: string
}

const CATALOGUE: CatalogueEntry[] = [
  {
    id: 'fortnox', name: 'Fortnox', monogram: 'FX',
    gradient: 'linear-gradient(135deg, oklch(0.6 0.14 158) 0%, oklch(0.5 0.13 162) 100%)',
    category: 'Finance', tags: ['Invoicing', 'ERP'],
    description: 'Articles, customers, projects, invoices, vouchers and payments',
    href: '/settings/integrations/fortnox',
  },
  {
    id: 'hailey', name: 'Hailey HR', monogram: 'HR',
    gradient: 'linear-gradient(135deg, oklch(0.62 0.18 285) 0%, oklch(0.5 0.2 290) 100%)',
    category: 'People', tags: ['HRIS'],
    description: 'Employee master data and people directory',
    href: '/settings/integrations/hailey',
  },
  {
    id: 'azure-devops', name: 'Azure DevOps', monogram: 'ADO',
    gradient: 'linear-gradient(135deg, oklch(0.55 0.18 250) 0%, oklch(0.42 0.2 260) 100%)',
    category: 'Engineering', tags: ['Work items', 'Projects'],
    description: 'Projects, work items and time tracking against ADO backlog items',
    href: '/settings/integrations/azure-devops',
  },
  {
    id: 'timetracker', name: 'TimeTracker', monogram: 'TT',
    gradient: 'linear-gradient(135deg, oklch(0.68 0.18 55) 0%, oklch(0.55 0.19 40) 100%)',
    category: 'Time', tags: ['Time entries', 'Transition sync'],
    description: 'Historical time entries, projects and users — synced during cutover to Forge time tracking',
    href: '/settings/integrations/timetracker',
  },
  {
    id: 'entra', name: 'Microsoft Entra ID', monogram: 'ID',
    gradient: 'linear-gradient(135deg, oklch(0.6 0.18 250) 0%, oklch(0.45 0.2 258) 100%)',
    category: 'Identity', tags: ['SSO', 'SCIM'],
    description: 'Single sign-on, group sync and access provisioning',
  },
  {
    id: 'fabric', name: 'Microsoft Fabric', monogram: 'FB',
    gradient: 'linear-gradient(135deg, oklch(0.62 0.13 200) 0%, oklch(0.5 0.14 210) 100%)',
    category: 'Data', tags: ['Lakehouse', 'Pipelines'],
    description: 'Data workspaces, capacities and pipeline runs',
  },
  {
    id: 'stripe', name: 'Stripe', monogram: 'S',
    gradient: 'linear-gradient(135deg, oklch(0.55 0.2 285) 0%, oklch(0.42 0.22 290) 100%)',
    category: 'Finance', tags: ['Payments'],
    description: 'Subscription billing and payment events',
  },
  {
    id: 'slack', name: 'Slack', monogram: '#',
    gradient: 'linear-gradient(135deg, oklch(0.7 0.16 30) 0%, oklch(0.58 0.18 22) 100%)',
    category: 'Notifications', tags: ['Alerts'],
    description: 'Route alerts, approvals and digests to channels',
  },
  {
    id: 'azure-cost', name: 'Azure Cost', monogram: 'ACM',
    gradient: 'linear-gradient(135deg, oklch(0.58 0.16 245) 0%, oklch(0.45 0.18 255) 100%)',
    category: 'Finance', tags: ['Cost analytics', 'Daily sync'],
    description: 'Daily per-resource cost data from Azure Cost Management, synced via service principal',
    href: '/settings/integrations/azure-cost',
  },
  {
    id: 'clementine', name: 'Clementine', monogram: 'CLM',
    gradient: 'linear-gradient(135deg, oklch(0.72 0.052 198) 0%, oklch(0.56 0.062 202) 100%)',
    category: 'Finance', tags: ['Budget', 'Forecast'],
    description: 'Budget and forecast data synced from Clementine via fortnoxdata database',
    href: '/settings/integrations/clementine',
  },
  {
    id: 'calendarific', name: 'Calendarific', monogram: 'CL',
    gradient: 'linear-gradient(135deg, oklch(0.68 0.17 45) 0%, oklch(0.55 0.18 38) 100%)',
    category: 'Calendar', tags: ['Holidays'],
    description: 'Import public holidays for any country to populate the holiday calendar',
    href: '/settings/integrations/calendarific',
  },
]

type Integration = CatalogueEntry & {
  status:  'connected' | 'available'
  health:  'healthy' | 'warning' | null
  lastSync: string | null
  cadence:  string | null
  metrics:  { label: string; value: number }[]
}

function relativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  return new Date(iso).toLocaleDateString('sv-SE')
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function IntegrationsPage() {
  const [filter, setFilter] = useState<'all' | 'connected' | 'available'>('all')
  const [search, setSearch] = useState('')

  const { data: fortnox     } = useQuery({ queryKey: ['fortnox',      'status'], queryFn: () => api.get<FortnoxStatus>('/fortnox/status')             })
  const { data: hailey      } = useQuery({ queryKey: ['hailey',       'status'], queryFn: () => api.get<HaileyStatus>('/hailey/status')               })
  const { data: ado         } = useQuery({ queryKey: ['azure-devops', 'status'], queryFn: () => api.get<{ connected: boolean; projectCount: number; workItemCount: number; lastSyncAt: string | null; lastSyncStatus: string | null }>('/azure-devops/status') })
  const { data: timetracker } = useQuery({ queryKey: ['timetracker',  'status'], queryFn: () => api.get<TimeTrackerStatus>('/timetracker/status')      })
  const { data: azureCost   } = useQuery({ queryKey: ['azure-cost',   'status'], queryFn: () => api.get<AzureCostStatus>('/azure-cost/status')         })
  const { data: calendarific   } = useQuery({ queryKey: ['calendarific',  'status'], queryFn: () => api.get<CalendarificStatus>('/settings/calendarific/status')        })
  const { data: clementine    } = useQuery({ queryKey: ['clementine',    'status'], queryFn: () => api.get<ClementineStatus>('/clementine-budget/status')               })

  const integrations: Integration[] = CATALOGUE.map(c => {
    if (c.id === 'fortnox' && fortnox?.connected) {
      return {
        ...c, status: 'connected', health: 'healthy',
        lastSync: relativeTime(fortnox.lastSync), cadence: 'Every 15 min',
        metrics: [
          { label: 'Art.',  value: fortnox.articleCount       },
          { label: 'Cust.', value: fortnox.customerCount      },
          { label: 'Inv.',  value: fortnox.invoiceCount ?? 0  },
          { label: 'Vou.',  value: fortnox.voucherCount ?? 0  },
          { label: 'Pay.',  value: fortnox.paymentCount ?? 0  },
        ],
      }
    }
    if (c.id === 'hailey' && hailey?.connected) {
      return {
        ...c, status: 'connected', health: 'healthy',
        lastSync: relativeTime(hailey.lastSync), cadence: 'Every 30 min',
        metrics: [{ label: 'Employees', value: hailey.employeeCount }],
      }
    }
    if (c.id === 'azure-devops' && ado?.connected) {
      return {
        ...c, status: 'connected', health: ado.lastSyncStatus === 'success' ? 'healthy' : 'warning',
        lastSync: relativeTime(ado.lastSyncAt), cadence: 'Daily at 4 AM',
        metrics: [
          { label: 'Projects',   value: ado.projectCount  },
          { label: 'Work items', value: ado.workItemCount },
        ],
      }
    }
    if (c.id === 'timetracker' && timetracker?.connected) {
      return {
        ...c, status: 'connected', health: timetracker.lastSyncStatus === 'failed' ? 'warning' : 'healthy',
        lastSync: relativeTime(timetracker.lastSyncAt ?? null), cadence: 'Nightly at 03:00',
        metrics: [
          { label: 'Users',    value: timetracker.userCount      ?? 0 },
          { label: 'Projects', value: timetracker.projectCount   ?? 0 },
          { label: 'Entries',  value: timetracker.timeEntryCount ?? 0 },
        ],
      }
    }
    if (c.id === 'azure-cost' && azureCost?.configured) {
      return {
        ...c, status: 'connected', health: azureCost.lastSyncStatus === 'error' ? 'warning' : 'healthy',
        lastSync: relativeTime(azureCost.lastSyncAt), cadence: 'Daily at 06:00 UTC',
        metrics: [
          { label: 'Days',  value: azureCost.syncedDays ?? 0 },
          { label: 'Rows',  value: azureCost.rowCount   ?? 0 },
        ],
      }
    }
    if (c.id === 'clementine' && clementine?.configured) {
      return {
        ...c, status: 'connected', health: clementine.lastSyncStatus === 'error' ? 'warning' : 'healthy',
        lastSync: relativeTime(clementine.lastSyncAt ?? null), cadence: 'On-demand',
        metrics: [
          { label: 'Versions',  value: clementine.forecastVersionCount ?? 0 },
          { label: 'Forecasts', value: clementine.forecastCount         ?? 0 },
          { label: 'Cost ctrs', value: clementine.costCenterCount       ?? 0 },
        ],
      }
    }
    // Calendarific is on-demand: "connected" simply means the API key is
    // present in app settings. No lastSync/metrics — the catalogue tile only
    // needs to stop saying "Not connected" when the key is configured.
    if (c.id === 'calendarific' && calendarific?.configured) {
      return {
        ...c, status: 'connected', health: 'healthy',
        lastSync: null, cadence: 'On-demand',
        metrics: [],
      }
    }
    return { ...c, status: 'available', health: null, lastSync: null, cadence: null, metrics: [] }
  })

  const filtered = integrations.filter(i => {
    if (filter === 'connected' && i.status !== 'connected') return false
    if (filter === 'available' && i.status !== 'available') return false
    if (search && !i.name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })
  const connected = filtered.filter(i => i.status === 'connected')
  const available = filtered.filter(i => i.status !== 'connected')

  const totalConnected = integrations.filter(i => i.status === 'connected').length
  const totalHealthy   = integrations.filter(i => i.health === 'healthy').length
  const totalWarning   = integrations.filter(i => i.health === 'warning').length
  const totalRecords   = (fortnox?.articleCount ?? 0) + (fortnox?.customerCount ?? 0) + (hailey?.employeeCount ?? 0) + (ado?.workItemCount ?? 0) + (azureCost?.rowCount ?? 0)

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Settings</div>
          <h1 className="page-title">Integrations</h1>
          <div className="page-subtitle">Manage external data sources, identity providers and notification routes.</div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn btn-ghost btn-sm"><HelpCircle size={14} />Docs</button>
          <button className="btn btn-primary btn-sm"><Plus size={14} />Browse catalogue</button>
        </div>
      </div>

      {/* Health summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <SummaryTile label="Connected"       value={totalConnected}                       icon={<Link2        size={18} />} tone="brand"   />
        <SummaryTile label="Healthy"         value={totalHealthy}                         icon={<CheckCircle2 size={18} />} tone="ok"      />
        <SummaryTile label="Needs attention" value={totalWarning}                         icon={<AlertTriangle size={18} />} tone="warn"    />
        <SummaryTile label="Records synced"  value={totalRecords.toLocaleString('sv-SE')} icon={<RefreshCw    size={18} />} tone="neutral" mono />
      </div>

      {/* Toolbar */}
      <div className="row" style={{ marginBottom: 16, gap: 10 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 10,
          height: 32, border: '1px solid var(--ns-border)', borderRadius: 6,
          background: 'var(--ns-surface)', width: 280,
        }}>
          <Search size={14} style={{ color: 'var(--ns-text-subtle)', flexShrink: 0 }} />
          <input
            placeholder="Search integrations…" value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, height: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: 13 }}
          />
        </div>
        <div className="row" style={{ gap: 4 }}>
          {(['all', 'connected', 'available'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`chip${filter === f ? ' active' : ''}`}>
              {f === 'all' ? 'All' : f === 'connected' ? 'Connected' : 'Available'}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm"><SlidersHorizontal size={14} />Categories</button>
      </div>

      {/* Connected */}
      {connected.length > 0 && (
        <>
          <SectionHeader label="Connected" count={connected.length} />
          <div className="integration-grid">
            {connected.map(i => <IntegrationCard key={i.id} item={i} />)}
          </div>
        </>
      )}

      {/* Available */}
      {available.length > 0 && (
        <>
          <SectionHeader label="Available to connect" count={available.length} />
          <div className="integration-grid">
            {available.map(i => <IntegrationCard key={i.id} item={i} />)}
          </div>
        </>
      )}
    </div>
  )
}

// ── Shared primitives ──────────────────────────────────────────────────────

export function BrandGlyph({ monogram, gradient, size = 42 }: { monogram: string; gradient: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: Math.round(size * 0.24),
      background: gradient, color: 'white', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Inter', sans-serif", fontWeight: 700,
      fontSize: Math.round(size * 0.33), letterSpacing: '-0.04em',
      boxShadow: 'inset 0 1px 0 oklch(1 0 0 / 0.18)',
    }}>{monogram}</div>
  )
}

function SummaryTile({ label, value, icon, tone, mono }: {
  label: string; value: number | string; icon: React.ReactNode
  tone: 'brand' | 'ok' | 'warn' | 'neutral'; mono?: boolean
}) {
  const toneMap = {
    brand:   { bg: 'var(--ns-brand-soft)',  fg: 'var(--ns-brand-strong)' },
    ok:      { bg: 'var(--ns-ok-soft)',     fg: 'var(--ns-ok)'           },
    warn:    { bg: 'var(--ns-warn-soft)',   fg: 'var(--ns-warn)'         },
    neutral: { bg: 'var(--ns-surface-2)',   fg: 'var(--ns-text-muted)'   },
  }[tone]

  return (
    <div className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: toneMap.bg, color: toneMap.fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div className="muted" style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.02em', marginBottom: 2 }}>{label}</div>
        <div className={mono ? 'mono tabular' : ''} style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.1 }}>{value}</div>
      </div>
    </div>
  )
}

function SectionHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className="row" style={{ margin: '8px 0 12px', gap: 10, alignItems: 'baseline' }}>
      <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600, letterSpacing: '0.02em' }}>{label}</h3>
      <span className="tabular" style={{ fontSize: 11, color: 'var(--ns-text-subtle)', fontWeight: 500 }}>{count}</span>
      <div style={{ flex: 1, height: 1, background: 'var(--ns-border)' }} />
    </div>
  )
}

function IntegrationCard({ item }: { item: Integration }) {
  const isConnected = item.status === 'connected'

  const card = (
    <button className="integration-card">
      <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
        <BrandGlyph monogram={item.monogram} gradient={item.gradient} size={42} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 13.5, letterSpacing: '-0.005em' }}>{item.name}</span>
            <span className="muted" style={{ fontSize: 11, fontWeight: 500 }}>· {item.category}</span>
          </div>
          <p className="muted" style={{
            margin: '3px 0 0', fontSize: 12.5, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{item.description}</p>
        </div>
        <ChevronRight size={14} style={{ color: 'var(--ns-text-subtle)', marginTop: 4, flexShrink: 0 }} />
      </div>

      <div className="row" style={{ marginTop: 10, gap: 4, flexWrap: 'wrap' }}>
        {item.tags.map(t => (
          <span key={t} style={{
            fontSize: 10.5, padding: '1px 7px', borderRadius: 4,
            background: 'var(--ns-surface-2)', color: 'var(--ns-text-muted)',
            border: '1px solid var(--ns-border)', fontWeight: 500,
          }}>{t}</span>
        ))}
      </div>

      <div className="integration-card-footer">
        {isConnected ? (
          <>
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: item.metrics.length > 0 ? 10 : 0 }}>
              <span className="row" style={{
                gap: 6, fontSize: 12, fontWeight: 500,
                color: item.health === 'healthy' ? 'var(--ns-ok)' : 'var(--ns-warn)',
              }}>
                <span className={`dot ${item.health === 'healthy' ? 'dot-ok' : 'dot-warn'}`} style={{ width: 7, height: 7 }} />
                {item.health === 'healthy' ? 'Healthy' : 'Degraded'}
              </span>
              {item.lastSync && (
                <span className="row" style={{ gap: 5, color: 'var(--ns-text-muted)', fontSize: 11.5 }}>
                  <RefreshCw size={11} /><span className="tabular">{item.lastSync}</span>
                </span>
              )}
            </div>
            {item.metrics.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${item.metrics.length}, 1fr)`, gap: 4 }}>
                {item.metrics.map(m => (
                  <div key={m.label}>
                    <div className="mono tabular" style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em' }}>{m.value.toLocaleString()}</div>
                    <div className="muted" style={{ fontSize: 10.5, marginTop: 1 }}>{m.label}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="muted" style={{ fontSize: 11.5 }}>Not connected</span>
            <span style={{ fontSize: 11.5, color: 'var(--ns-brand-strong)', fontWeight: 500 }}>Connect →</span>
          </div>
        )}
      </div>
    </button>
  )

  if (item.href) {
    return (
      <Link to={item.href as '/settings/integrations/fortnox' | '/settings/integrations/hailey' | '/settings/integrations/azure-devops' | '/settings/integrations/timetracker' | '/settings/integrations/calendarific' | '/settings/integrations/azure-cost' | '/settings/integrations/clementine'} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        {card}
      </Link>
    )
  }
  return card
}
